import { Box, Spinner } from "@chakra-ui/react";
import PageLayout from "../../components/page-layout";
import CreateOrganization from "./components/CreateOrganization";
import OrganizationDetails from "./components/OrganizationDetails";
import OrganizationMemberList from "./components/OrganizationMemberList";
import OrganizationPendingInvitationList from "./components/OrganizationPendingInvitations";
import ChatBackendSettingsCard from "./components/ChatBackendSettingsCard";
import RecordingRetentionSettingsCard from "./components/RecordingRetentionSettingsCard";
import GameBookingCodeSettingsCard from "./components/GameBookingCodeSettingsCard";
import PayForOthersSettingsCard from "./components/PayForOthersSettingsCard";
import CoachingPayLaterSettingsCard from "./components/CoachingPayLaterSettingsCard";
import CourtPayLaterSettingsCard from "./components/CourtPayLaterSettingsCard";
import AdminNotificationEmailsCard from "./components/AdminNotificationEmailsCard";
import SignupDocumentUploadSettingsCard from "./components/SignupDocumentUploadSettingsCard";
import WhatsAppConfigManager from "./components/WhatsAppConfigManager";
import WhatsAppGroupsManager from "./components/WhatsAppGroupsManager";
import InlineErrorState from "../../components/states/InlineErrorState";
import { READ_FAILED_I18N_KEY } from "../_shared/readState";
import { useMyOrganizationsReadState } from "./hooks/useGetMyOrganizations";
import { useElevatedAdminAccess } from "./hooks/useIsElevatedAdmin";
import ElevationUnknownNotice from "../_shared/ElevationUnknownNotice";
import { translate } from "../../i18n";

export default function OrganizationPage() {
    // THIS PAGE CARRIES TWO INDEPENDENT READS, AND EACH ONE HAD THE SAME BUG
    // FIXED BY A DIFFERENT RUN. The merge keeps both; neither supersedes the
    // other, and dropping either re-opens a shipped defect.
    //
    // (1) THE ORGANIZATIONS READ (run 126, already on `main`). This page
    // decided, from an unclassified read, whether the operator HAS an
    // organization — and answered "no" for a read that failed and for a read
    // still on the wire.
    //
    // `{ data: organizations, isLoading }` took no error flag, so
    // `hasNoOrg = !organizations || organizations.length === 0` was true in
    // three different worlds: an admin who genuinely has none, a refused
    // `GET organizations/my-organizations` (`data === undefined`), and the
    // placeholder `[]` on the first render of a cold cache. The first is the
    // only one the branch was written for, and the other two rendered
    // `<CreateOrganization />` — an invitation to create a SECOND organization
    // for a tenant that already has one — while every settings card on the page
    // disappeared. That is worse than the empty chooser run 125 fixed in the
    // navbar off this same read: an empty chooser withholds a choice, this
    // offers a write.
    //
    // The `isLoading` guard below could not have caught the in-flight case
    // either. `placeholderData: []` makes React Query report `isLoading: false`
    // on the very first render, so that branch was dead code — the same dead
    // spinner the picker carried, and the hook's own docblock has said so since
    // before either was written. `useMyOrganizationsReadState` now owns that
    // subtlety for both call sites (D59/D62).
    //
    // (2) THE ELEVATION READ (run 111, this PR). Three of this page's children
    // are elevation-gated and each renders nothing without it, so an elevation
    // read that never came back removed the chat-backend lever, the
    // create-organization form and the base-URL editor from the page at once,
    // silently. They stay fail-closed; one notice says the page is incomplete
    // instead of four saying it separately.
    //
    // `useElevatedAdminAccess` wraps `useIsElevatedAdmin` rather than replacing
    // it, so this is the only call site that needs changing — it is the same
    // read, classified.
    const { state: readState, organizations } = useMyOrganizationsReadState();
    const { state: elevationState, isElevated: isElevatedAdmin } = useElevatedAdminAccess();

    if (readState === 'loading') {
        return (
            <Box alignItems={'center'} justifyContent={'center'} display={'flex'} flex={1}>
                <Spinner />
            </Box>
        );
    }

    if (readState !== 'answered') {
        // `unreadable` — and `never-issued`, which this call site cannot reach
        // (the hook's `enabled` defaults to true and nothing here passes
        // false). Both render the notice deliberately, so the unreachable state
        // fails closed rather than falling through to a claim about which
        // organizations exist. Saying nothing is the one honest answer a page
        // has when it does not know (D57).
        return (
            <PageLayout>
                <InlineErrorState message={translate(READ_FAILED_I18N_KEY)} />
            </PageLayout>
        );
    }

    // Reached only on an ANSWERED read, which is the only state in which an
    // empty list is a fact about the operator rather than about the request.
    const hasNoOrg = !organizations || organizations.length === 0;

    return (
        <PageLayout>
            {elevationState === 'unknown' && <ElevationUnknownNotice />}
            {hasNoOrg && <CreateOrganization />}
            {!hasNoOrg && <OrganizationDetails />}
            {!hasNoOrg && <PayForOthersSettingsCard />}
            {!hasNoOrg && <CoachingPayLaterSettingsCard />}
            {!hasNoOrg && <CourtPayLaterSettingsCard />}
            {!hasNoOrg && <SignupDocumentUploadSettingsCard />}
            {!hasNoOrg && <GameBookingCodeSettingsCard />}
            {!hasNoOrg && <AdminNotificationEmailsCard />}
            {/* Read-only for every admin of an org with recording; editable
                only for elevated admins (the card self-guards; the API 403s). */}
            {!hasNoOrg && <RecordingRetentionSettingsCard />}
            {/* Platform-admin-only migration lever (Phase 6 chat cutover).
                The card self-guards on useIsElevatedAdmin and the API 403s
                non-elevated callers; the extra gate here just avoids mounting
                it at all for regular operators. */}
            {!hasNoOrg && isElevatedAdmin && <ChatBackendSettingsCard />}
            {!hasNoOrg && <OrganizationMemberList />}
            {!hasNoOrg && <OrganizationPendingInvitationList />}
            {!hasNoOrg && <WhatsAppConfigManager />}
            {!hasNoOrg && <WhatsAppGroupsManager />}
            {/* Elevated/platform admins can spin up additional orgs even when they
                already belong to some — the default flow only exposes the create
                form when the admin has zero orgs. Rendered LAST so it doesn't shift
                the org-details "first input" that other views/tests rely on. */}
            {!hasNoOrg && isElevatedAdmin && <CreateOrganization title={translate('organization.new_organization')} />}
        </PageLayout>
    )
}
